import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterContextData } from 'contexts/FilterContext';
import { isPpaPerfSummaryScreen, PPA_HOME_ROUTE } from 'config/urls';
import { usePpaDefaultTimePeriod } from 'hooks/ppa/usePpaDefaultTimePeriod';

function parseTimePeriodSelection(timePeriodSelection?: string) {
    try {
        return JSON.parse(timePeriodSelection ?? '{}');
    } catch {
        return {};
    }
}

function hasValidTimestamps(start?: number, end?: number) {
    return (
        Number.isFinite(start) &&
        Number.isFinite(end) &&
        (start ?? 0) > 0 &&
        (end ?? 0) > 0
    );
}

/**
 * Resolves PPA query filters and time period.
 * Performance Summary uses inherited scope until the user applies global filters.
 */
export function usePpaQueryFilters() {
    const { pathname } = useLocation();
    const screenName = pathname.slice(pathname.lastIndexOf('/') + 1);
    const isPpaHomeRoute = pathname.includes(PPA_HOME_ROUTE);
    const isPerfSummaryRoute = isPpaPerfSummaryScreen(screenName);

    const {
        ppaScopeFilterIds,
        ppaScopeFilterLoading,
        idForRestOfComponent,
        idForRestOfComponentLoading,
        timePeriodSelection,
        ppaGlobalFilterApplied,
    } = useContext(FilterContextData);

    const {
        startTimestamp: defaultStartTimestamp,
        endTimestamp: defaultEndTimestamp,
        hasValidTimePeriod: hasDefaultTimePeriod,
        isLoading: defaultTimePeriodLoading,
    } = usePpaDefaultTimePeriod();

    const useGlobalFilterForQueries = isPerfSummaryRoute && ppaGlobalFilterApplied;

    const scopeDim1Ids = ppaScopeFilterIds?.dim1?.ids ?? [];
    const scopeDim2Ids = ppaScopeFilterIds?.dim2?.ids ?? [];
    const globalDim1Ids = idForRestOfComponent?.dim1?.ids ?? [];
    const globalDim2Ids = idForRestOfComponent?.dim2?.ids ?? [];

    const dim1_id_list = useGlobalFilterForQueries ? globalDim1Ids : scopeDim1Ids;
    const dim2_id_list = useGlobalFilterForQueries ? globalDim2Ids : scopeDim2Ids;
    const hasResolvedFilterIds = dim1_id_list.length > 0 || dim2_id_list.length > 0;

    const parsedGlobalTimePeriod = useMemo(
        () => parseTimePeriodSelection(timePeriodSelection),
        [timePeriodSelection],
    );
    const globalStartTimestamp = parsedGlobalTimePeriod?.start_timestamp;
    const globalEndTimestamp = parsedGlobalTimePeriod?.end_timestamp;
    const hasGlobalTimePeriod = hasValidTimestamps(globalStartTimestamp, globalEndTimestamp);

    const startTimestamp = useGlobalFilterForQueries && hasGlobalTimePeriod
        ? globalStartTimestamp
        : defaultStartTimestamp;
    const endTimestamp = useGlobalFilterForQueries && hasGlobalTimePeriod
        ? globalEndTimestamp
        : defaultEndTimestamp;
    const hasValidTimePeriod = useGlobalFilterForQueries
        ? hasGlobalTimePeriod || hasDefaultTimePeriod
        : hasDefaultTimePeriod;

    const filtersLoading = useGlobalFilterForQueries
        ? ppaScopeFilterLoading || idForRestOfComponentLoading
        : ppaScopeFilterLoading;

    return {
        isPpaHomeRoute,
        isPerfSummaryRoute,
        useGlobalFilterForQueries,
        dim1_id_list,
        dim2_id_list,
        hasResolvedFilterIds,
        startTimestamp,
        endTimestamp,
        hasValidTimePeriod,
        filtersLoading,
        defaultTimePeriodLoading,
    };
}
